import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { CommissioningTags, GatewayType } from '@vi-iot/client';

import {
  GATEWAY_COMMISSIONING_DEVICE,
  GATEWAY_COMMISSIONING_FLOW,
  GATEWAY_COMMISSIONING_STEP,
  GatewayCommissioningStore,
  HeatingType,
  TransferInstallationFormData,
} from '@store';

import {
  AddressForm,
  GatewayForm,
  OmaFunctionForm,
  OwnerAdministratorForm,
} from './transfer-installation.helper';

@Component({
  selector: 'app-transfer-installation',
  templateUrl: './transfer-installation.component.html',
  styleUrls: ['./transfer-installation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TransferInstallationComponent implements OnInit, OnDestroy {
  @Output() previousStep = new EventEmitter();
  @Input() selectedDevice: GATEWAY_COMMISSIONING_DEVICE;

  readonly deviceType = GATEWAY_COMMISSIONING_DEVICE;
  readonly commissioningStep = GATEWAY_COMMISSIONING_STEP;
  readonly commissioningFlow = GATEWAY_COMMISSIONING_FLOW;

  commissioningFlowSelected: GATEWAY_COMMISSIONING_FLOW;
  isGatewayFormValid: boolean;
  isDescriptionValid: boolean;
  isAddressFormValid: boolean;
  isEmailFormValid: boolean;
  formData: TransferInstallationFormData;

  // Residential
  isOmaFunctionFormValid: boolean;
  selectedHeatingType: HeatingType;
  isGrandmaFunctionSelected = false;
  isVitoconnectOpenTherm = false;

  // Commercial
  isOwnerOfTheInstallationSelected = true;

  private ngUnsubscribe$: Subject<void> = new Subject<void>();
  public isMissingCertificateActive: boolean;
  constructor(protected gatewayCommissioningStore: GatewayCommissioningStore) {}

  ngOnInit(): void {
    this.isMissingCertificate();
    this.gatewayCommissioningStore
      .isGatewayOfType(GatewayType.VitoconnectOpenTherm)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((isVitoconnectOpenTherm) => {
        this.isVitoconnectOpenTherm = isVitoconnectOpenTherm;
      });

    this.gatewayCommissioningStore.commissioningFlow$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((commissioningFlow) => {
        this.commissioningFlowSelected = commissioningFlow;
        this.isDescriptionValid = commissioningFlow === GATEWAY_COMMISSIONING_FLOW.RESIDENTIAL;
      });

    this.gatewayCommissioningStore.formData$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((formData) => {
        this.formData = formData;
        this.selectedHeatingType = formData.heatingType;
      });
  }

  transfer(): void {
    this.gatewayCommissioningStore.transferInstallation({ withoutOwner: false });
  }

  transferOMA(): void {
    this.gatewayCommissioningStore.transferInstallation({ withoutOwner: true });
  }

  commercialTransfer(): void {
    this.gatewayCommissioningStore.commercialRegisterGateway({
      withoutInvitation: this.isOwnerOfTheInstallationSelected,
    });
  }

  previousStepClicked(step: GATEWAY_COMMISSIONING_STEP): void {
    if (step === this.commissioningStep.SELECT_INSTALLATION_TYPE) {
      this.gatewayCommissioningStore.clearState();
    } else {
      this.gatewayCommissioningStore.setActiveStep({
        activeStep: step,
      });
    }
    this.previousStep.emit();
  }

  onChangeHeatingType(type: HeatingType): void {
    this.selectedHeatingType = type;

    this.gatewayCommissioningStore.setFormData({
      heatingType: type,
    });
  }

  onGatewayDataChange(gatewayData: GatewayForm): void {
    this.isGatewayFormValid = gatewayData.isGatewayFormValid;
  }

  onDescriptionChange(data: { isValid: boolean; description: string }): void {
    this.gatewayCommissioningStore.setFormData({
      description: data.description,
    });
    this.isDescriptionValid =
      (this.commissioningFlowSelected === GATEWAY_COMMISSIONING_FLOW.RESIDENTIAL &&
        data.description === '') ||
      data.isValid;
  }

  onAddressDataChange(addressData: AddressForm): void {
    this.isAddressFormValid = addressData.isAddressFormValid;
  }

  onOmaFunctionDataChange(omaFunctionData: OmaFunctionForm): void {
    this.isOmaFunctionFormValid = omaFunctionData.isOmaFunctionFormValid;
    this.isEmailFormValid = omaFunctionData.isEmailFormValid;
    this.isGrandmaFunctionSelected = omaFunctionData.isGrandmaFunctionSelected;
  }

  onOwnerAdministratorDataChange(ownerAdministratorData: OwnerAdministratorForm): void {
    this.isOwnerOfTheInstallationSelected = ownerAdministratorData.isOwnerOfTheInstallationSelected;
    this.isEmailFormValid = ownerAdministratorData.isEmailFormValid;
  }

  isMissingCertificate(): void {
    this.gatewayCommissioningStore.gatewaysTags$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((tags) => {
        const shouldOpenModal = tags?.some(
          (tag) => tag === CommissioningTags.hb2MissingCertificate
        );
        if (shouldOpenModal) {
          this.isMissingCertificateActive = true;
        } else {
          this.isMissingCertificateActive = false;
        }
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
